<template>
  <div id="keyStats">
    <span class="float-right clickable mr-2"><i @click="closeStats()" class="fas fa-times close-button"></i></span>
    <p class="mb-0 text-center"><strong>{{ $store.state.keyStatsLabel }}</strong></p>
    <b-table v-if="showStats" stacked :items="statsData"></b-table>
    <HistogramSlider
      v-if="showStats"
      style="margin: 20px auto"
      :bar-height="100"
      :bar-width="2"
      :width="290"
      :data="statsValues"
      :forceEdges="false"
      :colors="['orange', 'orange']"
      :step=".001"
      :min="Math.round($store.state.keyStatsMin * 100) / 100"
      :max="Math.round($store.state.keyStatsMax * 100) / 100"
      :transitionDuration="100"
      :primaryColor="'orange'"
      :labelColor="'black'"
      :holderColor="'#056839'"
      :gridTextColor="'#333'"
      :grid="false"
      :type="'single'"
      :histSliderGap="30"
    />
  </div><!-- /#keyStats -->
</template>

<script>
import HistogramSlider from 'vue-histogram-slider'
import 'vue-histogram-slider/dist/histogram-slider.css'

export default {
  data () {
    return {
      showStats: false,
      statsData: [],
      statsValues: []
    }
  },
  created () {
    this.calculateStats()
  },
  destroyed () {
    const map = this.$store.state.mapObject
    const statsLayer = map.getLayer('statsLayer')
    if (statsLayer) {
      map.removeLayer('statsLayer')
    }
  },
  components: {
    HistogramSlider
  },
  methods: {
    mapStats (stats) {
      const map = this.$store.state.mapObject
      const statsLayer = map.getLayer('statsLayer')
      const filteredPolygonSource = map.getSource('filteredPolygonSource')
      const header = this.$store.state.dataset.vizSettings.colorHeader
      const features = this.$store.state.datasetGeoJson.features
      if (statsLayer) {
        map.removeLayer('statsLayer')
      }
      if (filteredPolygonSource) {
        map.removeSource('filteredPolygonSource')
      }
      if (stats.min === features[0].properties[header] && stats.max === features[features.length - 1].properties[header]) {
        console.log('Whole dataset selected. No highlight')
      } else {
        if (this.$store.state.dataset.vizSettings.type === 'polygon') {
          const filteredPolygonSourceData = {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: []
            }
          }
          features.forEach(featureItem => {
            if (featureItem.properties[header] >= stats.min && featureItem.properties[header] <= stats.max) {
              filteredPolygonSourceData.data.features.push(featureItem)
            }
          })
          map.addSource('filteredPolygonSource', filteredPolygonSourceData)
          map.addLayer({
            id: 'statsLayer',
            type: 'fill',
            source: 'filteredPolygonSource',
            layout: {},
            paint: {
              'fill-color': 'red'
            }
          })
        } else if (this.$store.state.dataset.vizSettings.type === 'line') {
          map.addLayer({
            id: 'statsLayer',
            type: 'line',
            source: 'datasetPreviewGeojson',
            layout: {},
            paint: {
              'line-color': 'red',
              'line-width': this.$store.state.dataset.vizSettings.lineWidth ?? 2
            }
          })
          const filterMin = ['>=', ['number', ['get', header]], stats.min]
          const filterMax = ['<=', ['number', ['get', header]], stats.max]
          map.setFilter('statsLayer', ['all', filterMin, filterMax])
        } else {
          map.addLayer({
            id: 'statsLayer',
            type: 'circle',
            source: 'datasetPreviewGeojson',
            layout: {},
            paint: {
              'circle-radius': this.$store.state.dataset.vizSettings.circleRadius,
              'circle-color': 'red'
            }
          })
          const filterMin = ['>=', ['number', ['get', header]], stats.min]
          const filterMax = ['<=', ['number', ['get', header]], stats.max]
          map.setFilter('statsLayer', ['all', filterMin, filterMax])
        }
      }
    },
    calculateStats () {
      const stats = {}
      const features = this.$store.state.datasetGeoJson.features
      const header = this.$store.state.dataset.vizSettings.colorHeader
      features.sort((a, b) => a.properties[header] - b.properties[header])
      this.statsValues = []
      features.forEach(feature => {
        const value = feature.properties[header]
        if (value >= this.$store.state.keyStatsMin && value <= this.$store.state.keyStatsMax) {
          this.statsValues.push(feature.properties[header])
        }
      })
      const half = Math.floor(this.statsValues.length / 2)
      if (this.statsValues.length % 2) {
        stats.median = this.statsValues[half]
      } else {
        stats.median = (this.statsValues[half - 1] + this.statsValues[half]) / 2.0
      }
      const sum = this.statsValues.reduce((a, b) => a + b, 0)
      stats.mean = (sum / this.statsValues.length).toFixed(4) || 0
      stats.count = this.statsValues.length
      stats.sum = sum.toFixed(4) + ' (' + header + ')'
      stats.min = this.$store.state.keyStatsMin
      stats.max = this.$store.state.keyStatsMax
      stats.σ = Math.sqrt(this.statsValues.map(x => Math.pow(x - stats.mean, 2)).reduce((a, b) => a + b) / stats.count).toFixed(4)
      if (this.$store.state.userProfile.measurementSystem === 'Imperial') {
        stats.acres = (this.$store.state.keyStatsArea * 0.000247105).toFixed(2)
      } else if (this.$store.state.userProfile.measurementSystem === 'Metric') {
        stats.hectares = (this.$store.state.keyStatsArea / 10000).toFixed(2)
      }
      this.statsData.push(stats)
      this.showStats = true
      this.mapStats(stats)
    },
    closeStats () {
      this.$store.commit('setStateProperty', { property: 'showKeyStats', value: false })
    }
  }
}
</script>

<style scoped>
#keyStats {
  position: absolute;
  top: -400px;
  left: 0px;
  width: 100%;
  height: 390px;
  background-color: #eee;
  border-radius: 4px;
  padding: 2px 4px;
  box-shadow: 1px 3px 6px rgba(0,0,0,0.3);
  min-width: 100px;
  z-index: 1;
}

.close-button {
  font-size: 16px;
}
</style>
